// GarageBand's "choose a sound": a grid of instruments, each one tap away
// from being a track, each with a ▶ to hear it first. Presentation only —
// what a pick does is the page's.

import { AudioWaveform, Drum, Guitar, Piano, Play, Radio, Square, Waves, Zap, type LucideIcon } from "lucide-react";
import type { InstrumentId } from "@shared/instruments.js";
import { INSTRUMENT_CATALOG } from "@web-shared/lib/instrument-catalog";
import { cn } from "@web-shared/lib/utils";

const ICONS: Record<InstrumentId, LucideIcon> = {
  keys: Piano,
  kit: Drum,
  bass: Guitar,
  pluck: Zap,
  pad: Waves,
  lead: Radio,
  sine: AudioWaveform,
};

export function InstrumentPicker({
  current,
  onPick,
  onPreview,
  previewing,
  className,
}: {
  /** The lane's instrument now, marked (changing a track's sound). */
  current?: InstrumentId;
  onPick: (id: InstrumentId) => void;
  /** Hear the instrument; pressed again while it sounds, stops it. */
  onPreview?: (id: InstrumentId) => void;
  /** The instrument sounding now, if any. */
  previewing?: InstrumentId | null;
  className?: string;
}) {
  return (
    <div className={cn("grid grid-cols-2 gap-2 sm:grid-cols-3", className)}>
      {INSTRUMENT_CATALOG.map((entry) => {
        const Icon = ICONS[entry.id];
        const active = current === entry.id;
        return (
          <div key={entry.id} className="relative">
            <button
              type="button"
              onClick={() => onPick(entry.id)}
              className={cn(
                "flex h-full w-full flex-col items-start gap-2 rounded-xl border bg-panel p-3 text-left transition-colors",
                active ? "border-primary" : "border-panelline hover:border-foreground/30 hover:bg-white/[0.03]",
              )}
            >
              <Icon className={cn("size-6", active ? "text-primary" : "text-foreground/80")} />
              <span className="text-sm font-semibold">{entry.name}</span>
              <span className="text-[11px] leading-snug text-muted-foreground">{entry.blurb}</span>
            </button>
            {onPreview && (
              <button
                type="button"
                onClick={() => onPreview(entry.id)}
                aria-label={previewing === entry.id ? `Stop ${entry.name}` : `Hear ${entry.name}`}
                title={previewing === entry.id ? `Stop ${entry.name}` : `Hear ${entry.name}`}
                className={cn(
                  "absolute right-2 top-2 flex size-8 items-center justify-center rounded-full border bg-sunk",
                  previewing === entry.id ? "border-primary text-primary" : "border-panelline text-foreground/70 hover:text-foreground",
                )}
              >
                {previewing === entry.id ? <Square className="size-3 fill-current" /> : <Play className="size-3.5" />}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
